import type { IAdvertising } from '@src/@types/advertising';
import { MONETIZATION_TYPES } from '@src/constants/advertising';
import { FILTERS_DICTIONARIES_MAP, FILTERS_LABELS, FILTERS_NAMES, SORT_LABELS } from '@src/constants/filters';
import { itemLabelFactory, makeAdGEvent, makeMainGEvent, pushEvent } from '@src/utils/analytics';

const EVENT_CATEGORY = 'Микрокредиты';
const DEFAULT_EVENT_VALUE = 0;

const PAGE_VIEW_GA_STATE: { title: string; path: string } = {
    title: '',
    path: '',
};

export interface IAnalyticsItem {
    eventAction?: string;
    advertising?: IAdvertising;
    _id?: string;
    name: string;
    organization: {
        name: string;
        _id?: string;
    };
}

const getEventValueFromItem = (item: IAnalyticsItem) => {
    if (item.advertising) {
        return item.advertising.cost;
    }

    return undefined;
};

/** AD G_EVENTS * */
export const sendExtraFiltersOpenEvent = () => {
    pushEvent(
        makeAdGEvent({
            eventCategory: EVENT_CATEGORY,
            eventAction: 'Еще условия',
        }),
    );
};

const sendSortEvent = (value: string) => {
    pushEvent(
        makeAdGEvent({
            eventCategory: EVENT_CATEGORY,
            eventAction: 'Сортировка',
            eventLabel: value,
        }),
    );
};

const sendFilterEvent = (label: string, values: string | string[]) => {
    pushEvent(
        makeAdGEvent({
            eventCategory: EVENT_CATEGORY,
            eventAction: ['Фильтр', label],
            eventLabel: values,
        }),
    );
};

export const sendPaginationEvent = () => {
    pushEvent(
        makeAdGEvent({
            eventCategory: EVENT_CATEGORY,
            eventAction: 'Показать еще',
        }),
    );
};

export const sendChangeAmountFilterEvent = (amount: number) => {
    pushEvent(
        makeAdGEvent({
            eventCategory: EVENT_CATEGORY,
            eventAction: 'Фильтр|Мне нужно',
            // @ts-ignore
            eventLabel: amount,
        }),
    );
};

export const sendChangeTermFilterEvent = (term: string) => {
    pushEvent(
        makeAdGEvent({
            eventCategory: EVENT_CATEGORY,
            eventAction: 'Фильтр|На срок',
            eventLabel: term,
        }),
    );
};

export const sendChangeObtainingMethodFilterEvent = (obtainingMethod: string) => {
    pushEvent(
        makeAdGEvent({
            eventCategory: EVENT_CATEGORY,
            eventAction: 'Фильтр|Способ',
            eventLabel: obtainingMethod,
        }),
    );
};

export const sendUserWantFreeLoanEvent = () => {
    pushEvent(
        makeAdGEvent({
            eventCategory: EVENT_CATEGORY,
            eventAction: 'Фильтр|Хочу первый займ под 0%',
            eventLabel: 'TRUE',
        }),
    );
};

/** MAIN G_EVENTS * */

// @ts-ignore
export const sendSponsorClickEvent = (organizationName) => {
    pushEvent(
        makeMainGEvent({
            eventCategory: EVENT_CATEGORY,
            eventAction: 'Спонсор раздела',
            eventLabel: organizationName,
        }),
    );
};

const sendPaidClickEvent = (item: IAnalyticsItem) => {
    pushEvent(
        makeMainGEvent({
            eventCategory: EVENT_CATEGORY,
            eventAction: item.eventAction || item.advertising?.buttonEventAction || 'Платный клик',
            eventLabel: itemLabelFactory(item),
            eventValue: getEventValueFromItem(item),
        }),
    );
};

const sendGoToRequestEvent = (item: IAnalyticsItem) => {
    pushEvent(
        makeMainGEvent({
            eventCategory: EVENT_CATEGORY,
            eventAction: 'Переход к оформлению заявки',
            eventLabel: itemLabelFactory(item),
        }),
    );
};

export const sendClickOnExtraInformationEvent = (item: IAnalyticsItem) => {
    pushEvent(
        makeMainGEvent({
            eventCategory: EVENT_CATEGORY,
            eventAction: 'Переход в карточку',
            eventLabel: itemLabelFactory(item),
        }),
    );
};

export const sendOrganizationOpenEvent = (item: IAnalyticsItem) => {
    pushEvent(
        makeMainGEvent({
            eventCategory: EVENT_CATEGORY,
            eventAction: 'Переход в карточку МФО',
            eventLabel: itemLabelFactory(item),
        }),
    );
};

export const sendClickGetMoneyButtonEvent = (url: string) => {
    pushEvent(
        makeMainGEvent({
            eventCategory: EVENT_CATEGORY,
            eventAction: 'Кнопка|Получить деньги',
            eventLabel: url,
        }),
    );
};

const REQUESTS_MONETIZATIONS = new Set([
    MONETIZATION_TYPES.REQUEST_LONG,
    MONETIZATION_TYPES.REQUEST_SHORT,
    MONETIZATION_TYPES.APPROVAL,
]);

export const sendAdvertisingButtonClickEvent = (item: IAnalyticsItem) => {
    const { advertising } = item;

    if (advertising) {
        const {
            monetization: { kind },
        } = advertising;

        // todo: maybe this logic is not valid, talk with manager
        if (REQUESTS_MONETIZATIONS.has(kind)) {
            sendGoToRequestEvent(item);
        } else {
            sendPaidClickEvent(item);
        }
    }
};

// @ts-ignore
export const checkAndSendPageViewGAEvent = (title, path) => {
    if (title !== PAGE_VIEW_GA_STATE.title && path !== PAGE_VIEW_GA_STATE.path) {
        if (PAGE_VIEW_GA_STATE.title && PAGE_VIEW_GA_STATE.path) {
            pushEvent({
                event: 'pageViewGA',
                title,
                path,
            });
        }

        Object.assign(PAGE_VIEW_GA_STATE, { title, path });
    }
};

const getEventAction = (): string => 'Вход в ЛК|pop-up';

export const sendSuccessfulAuthorization = () => {
    pushEvent(
        makeMainGEvent({
            eventCategory: EVENT_CATEGORY,
            eventAction: getEventAction(),
            eventLabel: 'Авторизация по номеру',
        }),
    );
};

// @ts-ignore
const humanizeFilterValue = (key, value, dictionaries) => {
    // eslint-disable-next-line @typescript-eslint/init-declarations
    let fields;

    switch (key) {
        case FILTERS_NAMES.TERM: {
            // eslint-disable-next-line no-param-reassign
            value = typeof value === 'string' ? Number(value.replace(/[^0-9]/g, '')) : value;
            break;
        }

        default: {
            // @ts-ignore
            ({ fields } = dictionaries?.[FILTERS_DICTIONARIES_MAP[key]] || {});
        }
    }

    if (fields) {
        // `label` is polyfill for some custom fields (for example BANKS_RATINGS)
        // @ts-ignore
        const { name, label } = fields.find((item) => item.value === value) || { name: value };

        return name || label;
    }

    return value;
};
// @ts-ignore
const humanizeFilterLabel = (key) => FILTERS_LABELS[key] || key;

export const sendAnalytics = (() => {
    const timers = {};

    // @ts-ignore
    return (key, value, dictionaries) => {
        // @ts-ignore
        clearTimeout(timers[key]);

        // @ts-ignore
        timers[key] = setTimeout(
            // @ts-ignore
            (key, value, dictionaries) => {
                switch (key) {
                    case FILTERS_NAMES.SORT: {
                        sendSortEvent(SORT_LABELS[value] || value);
                        break;
                    }
                    case FILTERS_NAMES.OFFERS_TYPE: {
                        if (!value) {
                            sendFilterEvent(humanizeFilterLabel(key), 'new');
                        } else {
                            sendFilterEvent(humanizeFilterLabel(key), humanizeFilterValue(key, value, dictionaries));
                        }
                        break;
                    }

                    default: {
                        sendFilterEvent(humanizeFilterLabel(key), humanizeFilterValue(key, value, dictionaries));
                    }
                }
            },
            800,
            key,
            value,
            dictionaries,
        );
    };
})();

export const sendSidebarClickEvent = (eventLabel: string, eventValue: number | undefined) => {
    pushEvent(
        makeMainGEvent({
            eventCategory: EVENT_CATEGORY,
            eventAction: 'Платный клик|Сайдбар',
            eventLabel,
            eventValue,
        }),
    );
};

export const sendAdditionalConditionsClickEvent = (eventLabel: string) => {
    pushEvent(
        makeAdGEvent({
            eventCategory: EVENT_CATEGORY,
            eventAction: 'Доп условия|pop-up',
            eventLabel,
        }),
    );
};

export const sendSortOnDropdownClickEvent = () => {
    pushEvent(
        makeAdGEvent({
            eventCategory: EVENT_CATEGORY,
            eventAction: 'Сортировка|Сортировать',
            eventLabel: null,
        }),
    );
};

export const sendSortOnDropdownOptionsClickEvent = (value: string) => {
    pushEvent(
        makeAdGEvent({
            eventCategory: EVENT_CATEGORY,
            eventAction: 'Сортировка|Сортировать',
            eventLabel: value,
        }),
    );
};

export const sendPromotionClickEvent = (organizationName: string) => {
    pushEvent(
        makeMainGEvent({
            eventCategory: EVENT_CATEGORY,
            eventAction: 'Платный клик|Акция',
            eventLabel: `${organizationName}|Акция`,
            eventValue: DEFAULT_EVENT_VALUE,
        }),
    );
};

export const sendAdblockDetectedEvent = () => {
    pushEvent(
        makeMainGEvent({
            eventCategory: EVENT_CATEGORY,
            eventAction: 'Визит|AdBlock',
            eventLabel: undefined,
        }),
    );
};

export const sendPhoneNumberEvent = () => {
    pushEvent(
        makeMainGEvent({
            eventCategory: EVENT_CATEGORY,
            eventAction: 'Ввод телефона|Показать вероятность одобрения',
            eventLabel: undefined,
        }),
    );
};

export const sendOpenAppEvent = () => {
    pushEvent(
        makeMainGEvent({
            eventCategory: EVENT_CATEGORY,
            eventAction: 'Скачать приложение',
            eventLabel: undefined,
        }),
    );
};
